import React from 'react';
import { Link } from 'react-router-dom';
import './HealthWelfare.css';

const HealthWelfare = () => {
  return (
    <div className="health-welfare-page">
      {/* Hero */}
      <section className="hw-hero">
        <div className="hw-hero__inner">
          <nav className="hw-hero__breadcrumb">
            <Link to="/">Home</Link>
            <span className="hw-hero__breadcrumb-sep">/</span>
            <span className="hw-hero__breadcrumb-current">Health &amp; Welfare LPA</span>
          </nav>
          <h1 className="hw-hero__title">Health &amp; Welfare LPA</h1>
        </div>
      </section>

      {/* What It Covers */}
      <section className="hw-section hw-section--white">
        <div className="hw-section__inner">
          <p className="hw-section__label">Overview</p>
          <h2 className="hw-section__title">What It Covers</h2>
          <p className="hw-section__desc">
            A Health and Welfare LPA authorises your chosen attorneys to make decisions about your
            personal welfare if you are unable to make those decisions yourself. This type of LPA
            covers a wide range of health and care matters, including:
          </p>
          <ul className="hw-list">
            <li>Medical treatment and procedures, including consent to or refusal of treatment</li>
            <li>Care arrangements, including the type and location of care you receive</li>
            <li>Where you live, including decisions about residential or nursing care</li>
            <li>Day-to-day welfare matters, such as diet, dress and daily routine</li>
            <li>
              Life-sustaining treatment (if expressly authorised within the LPA), including the
              authority to consent to or refuse treatment necessary to keep you alive
            </li>
          </ul>
          <div className="hw-note">
            <p>
              You may include preferences and instructions in your LPA to guide how your attorneys
              make decisions on your behalf. For example, you may express a preference for a
              particular type of care or instruct that certain treatments should not be administered.
            </p>
          </div>
        </div>
      </section>

      {/* What It Does Not Cover */}
      <section className="hw-section hw-section--alt">
        <div className="hw-section__inner">
          <p className="hw-section__label">Limitations</p>
          <h2 className="hw-section__title">What It Does Not Cover</h2>
          <p className="hw-section__desc">
            A Health and Welfare LPA is confined to personal welfare matters. There are important
            boundaries to the authority it confers:
          </p>
          <ul className="hw-list">
            <li>
              Financial matters: a Health and Welfare LPA does not authorise attorneys to manage your
              money, property or other financial affairs
            </li>
            <li>
              Decisions while you retain capacity: attorneys cannot make health and welfare decisions
              on your behalf while you are still able to make those decisions yourself
            </li>
            <li>
              Unlawful or improper decisions: attorneys may not make decisions that are illegal or
              that conflict with the principles of the Mental Capacity Act 2005
            </li>
            <li>
              Overriding valid advance decisions: if you have made a valid and applicable advance
              decision to refuse treatment, your attorneys cannot override it
            </li>
          </ul>
          <div className="hw-note">
            <p>
              All decisions made under a Health and Welfare LPA must be in your best interests and
              must comply with the Mental Capacity Act 2005. Attorneys must consider your past and
              present wishes, feelings, beliefs and values when making decisions.
            </p>
          </div>
        </div>
      </section>

      {/* When It Takes Effect */}
      <section className="hw-section hw-section--white">
        <div className="hw-section__inner">
          <p className="hw-section__label">Activation</p>
          <h2 className="hw-section__title">When It Takes Effect</h2>
          <p className="hw-section__desc">
            Unlike a Property and Financial Affairs LPA, a Health and Welfare LPA can only be used
            when you lack the mental capacity to make the relevant decision yourself.
          </p>
          <div className="hw-timeline">
            <div className="hw-timeline__item">
              <div className="hw-timeline__marker">1</div>
              <div className="hw-timeline__content">
                <h4>Registration with the OPG</h4>
                <p>
                  The LPA must first be registered with the Office of the Public Guardian. It has no
                  legal effect until registration is complete.
                </p>
              </div>
            </div>
            <div className="hw-timeline__item">
              <div className="hw-timeline__marker">2</div>
              <div className="hw-timeline__content">
                <h4>Capacity must be lacking</h4>
                <p>
                  Your attorneys can only act when you lack the capacity to make the specific
                  decision in question. This is a key distinction from the financial LPA.
                </p>
              </div>
            </div>
            <div className="hw-timeline__item">
              <div className="hw-timeline__marker">3</div>
              <div className="hw-timeline__content">
                <h4>Decision-specific assessment</h4>
                <p>
                  Capacity is assessed on a decision-specific and time-specific basis. You may lack
                  capacity for one decision but retain it for another. Each situation is considered
                  individually.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Risks of Not Having One */}
      <section className="hw-section hw-section--alt">
        <div className="hw-section__inner">
          <p className="hw-section__label">Why It Matters</p>
          <h2 className="hw-section__title">Risks of Not Having One</h2>
          <p className="hw-section__desc">
            Without a Health and Welfare LPA, your loved ones may face significant difficulties
            if you lose the ability to make decisions about your own care and treatment:
          </p>
          <div className="hw-cards">
            <div className="hw-card">
              <div className="hw-card__title">Family lacks authority</div>
              <div className="hw-card__text">
                Your family members will have no legal authority to make healthcare or welfare
                decisions on your behalf, regardless of how close the relationship.
              </div>
            </div>
            <div className="hw-card">
              <div className="hw-card__title">Healthcare professionals decide</div>
              <div className="hw-card__text">
                Medical and care decisions will be made by healthcare professionals who may not
                know your personal wishes, values or preferences.
              </div>
            </div>
            <div className="hw-card">
              <div className="hw-card__title">Disagreements cause distress</div>
              <div className="hw-card__text">
                Without a clear legal framework, disagreements between family members or between
                family and medical staff can arise, causing additional stress during an already
                difficult time.
              </div>
            </div>
            <div className="hw-card">
              <div className="hw-card__title">Court involvement may be required</div>
              <div className="hw-card__text">
                An application to the Court of Protection may be necessary to resolve disputes or
                to authorise specific decisions, which is costly, time-consuming and emotionally
                burdensome.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cancelling a Health & Welfare LPA */}
      <section className="hw-section hw-section--white">
        <div className="hw-section__inner">
          <p className="hw-section__label">Revocation</p>
          <h2 className="hw-section__title">Cancelling a Health &amp; Welfare LPA</h2>
          <p className="hw-section__desc">
            You may revoke your Health and Welfare LPA at any time, provided you retain the mental
            capacity to do so. The revocation process is the same as for a Property and Financial
            Affairs LPA.
          </p>
          <ul className="hw-list">
            <li>
              You must execute a formal Deed of Revocation, which is a written document confirming
              your intention to cancel the LPA.
            </li>
            <li>
              The Office of the Public Guardian must be notified so that the registration is
              cancelled.
            </li>
            <li>
              Your attorneys should be informed in writing that their authority has been revoked.
            </li>
            <li>
              Any third parties who have been relying on the LPA (such as healthcare providers and
              care homes) should also be notified.
            </li>
          </ul>
          <div className="hw-note">
            <p>
              If you wish to revoke your LPA, we recommend seeking legal advice to ensure the
              process is carried out correctly and that your interests are fully protected.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="hw-cta">
        <div className="hw-cta__inner">
          <h2 className="hw-cta__title">Protect Your Health and Welfare</h2>
          <p className="hw-cta__desc">
            Ensure your wishes are respected if you are ever unable to speak for yourself.
            Contact us to discuss putting a Health and Welfare LPA in place.
          </p>
          <Link to="/contact" className="btn btn-primary">
            Get in Touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HealthWelfare;
